/** Base error class for all Tonty Tech Interviews errors */
export class TontyError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly hint?: string,
  ) {
    super(message)
    this.name = 'TontyError'
  }
}

export class ScenarioNotFoundError extends TontyError {
  constructor(name: string) {
    super(
      `Scenario not found: ${name}`,
      'SCENARIO_NOT_FOUND',
      'Run `tonty-tech-interviews list` to see available scenarios',
    )
  }
}

export class ScenarioValidationError extends TontyError {
  constructor(
    message: string,
    public readonly issues: string[],
  ) {
    super(`Invalid scenario config: ${message}`, 'SCENARIO_VALIDATION_ERROR', issues.join('\n'))
  }
}

export class ProviderNotFoundError extends TontyError {
  static readonly installHints: Record<string, string> = {
    'claude-code': 'Install Claude Code: npm install -g @anthropic-ai/claude-code',
    'command-code': 'Install CommandCode: npm install -g command-code',
    'codex': 'Install Codex: npm install -g @openai/codex',
    'opencode': 'Install OpenCode: npm install -g opencode-ai',
    'kilo-code': 'Install Kilo Code: npm install -g kilo-code',
    'aider': 'Install Aider: pip install aider-chat',
    'antigravity': 'Install Antigravity: curl -fsSL https://antigravity.google.com/install.sh | bash',
    'open-interpreter': 'Install Open Interpreter: pip install open-interpreter',
    'amazon-q': 'Install Amazon Q CLI: npm install -g @amzn/q',
  }

  constructor(provider: string) {
    super(
      `${provider} is not installed`,
      'PROVIDER_NOT_FOUND',
      ProviderNotFoundError.installHints[provider] ?? `Install ${provider} and try again`,
    )
  }
}

export class SessionNotFoundError extends TontyError {
  constructor(id: string) {
    super(
      `Session not found: ${id}`,
      'SESSION_NOT_FOUND',
      'Run `tonty-tech-interviews sessions list` to see active sessions',
    )
  }
}

export class GitCloneError extends TontyError {
  constructor(repo: string, reason?: string) {
    super(
      `Failed to clone repository: ${repo}${reason ? ` — ${reason}` : ''}`,
      'GIT_CLONE_FAILED',
      'Check the repo URL and your network connection',
    )
  }
}

export class SetupError extends TontyError {
  constructor(command: string, reason?: string) {
    super(
      `Setup command failed: ${command}${reason ? ` — ${reason}` : ''}`,
      'SETUP_FAILED',
      'Check the scenario setup commands and try again',
    )
  }
}

export class ScenarioFetchError extends TontyError {
  constructor(url: string, reason?: string) {
    super(
      `Failed to fetch scenario from URL: ${url}${reason ? ` — ${reason}` : ''}`,
      'SCENARIO_FETCH_FAILED',
      'Check the URL is accessible and returns valid YAML',
    )
  }
}

export class UpdateError extends TontyError {
  constructor(reason?: string) {
    super(
      `Failed to update${reason ? ` — ${reason}` : ''}`,
      'UPDATE_FAILED',
      'Try updating manually: npm install -g tonty-tech-interviews@latest',
    )
  }
}
